import React, { useContext, useEffect, useRef, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { logo } from "../../assets";
import { FaAngleDoubleLeft, FaAngleDown, FaAngleUp } from "react-icons/fa";
import ClickOutside from "../../components/ClickOutside";
import { useSelector } from "react-redux";
import { GoDash } from "react-icons/go";
import { useGetMerchantPermissions } from "../../api/services/rolePermissionServices";
import { BusinessContext } from "../../context/BusinessContext";
import { getSidebarMenus } from "../../getSidebarMenus";
import Loader from "../../components/Loader";

const ChildLinkGroup = ({ activeCondition, children }) => {
  const [isOpen, setIsOpen] = useState(activeCondition);

  const handleClick = () => {
    setIsOpen(!isOpen);
  };

  return (
    <div className="flex flex-col">
      {children(handleClick, isOpen)}
      <div
        className={`overflow-hidden transition-[max-height] duration-300 ease-in-out ${
          isOpen ? "max-h-[500px]" : "max-h-0"
        }`}
      >
        {isOpen && <div className="pt-2">{children}</div>}
      </div>
    </div>
  );
};

const Sidebar = ({ sidebarOpen, setSidebarOpen }) => {
  const location = useLocation();
  const { pathname } = location;
  const trigger = useRef(null);
  const sidebar = useRef(null);

  const storedSidebarExpanded = localStorage.getItem("sidebar-expanded");
  const [sidebarExpanded, setSidebarExpanded] = useState(
    storedSidebarExpanded === null ? false : storedSidebarExpanded === "true"
  );

  const { user } = useSelector((state) => state.auth);
  const { data: merchantPermissions, isLoading } = useGetMerchantPermissions(
    user?.id
  );

  const isAuthorizedToAccessMenu = (menuPermission) => {
    if (!menuPermission) return true;
    const permissions = merchantPermissions?.data?.permissions || [];
    return permissions.some(
      (permission) => permission?.permissionName === menuPermission
    );
  };

  useEffect(() => {
    const clickHandler = ({ target }) => {
      if (!sidebar.current || !trigger.current) return;
      if (
        !sidebarOpen ||
        sidebar.current.contains(target) ||
        trigger.current.contains(target)
      )
        return;
      setSidebarOpen(false);
    };
    document.addEventListener("click", clickHandler);
    return () => document.removeEventListener("click", clickHandler);
  }, [sidebarOpen]);

  useEffect(() => {
    const keyHandler = ({ keyCode }) => {
      if (!sidebarOpen || keyCode !== 27) return;
      setSidebarOpen(false);
    };
    document.addEventListener("keydown", keyHandler);
    return () => document.removeEventListener("keydown", keyHandler);
  }, [sidebarOpen]);

  useEffect(() => {
    localStorage.setItem("sidebar-expanded", sidebarExpanded.toString());
    if (sidebarExpanded) {
      document.querySelector("body")?.classList.add("sidebar-expanded");
    } else {
      document.querySelector("body")?.classList.remove("sidebar-expanded");
    }
  }, [sidebarExpanded]);

  const handleLinkClick = () => {
    setSidebarOpen(false);
  };

  const { refundRequests, userBusinesses } = useSelector(
    (state) => state.business
  );
  const { activeBusiness } = useContext(BusinessContext);

  const activeBusinessObject = userBusinesses?.find(
    (business) => business.id === activeBusiness
  );

  const categories = activeBusinessObject?.Categories?.map((category) =>
    category.categoryName.toLowerCase()
  );

  const sideBarMenus = getSidebarMenus(activeBusiness, userBusinesses);

  return (
    <ClickOutside onClick={() => setSidebarOpen(false)} className="relative">
      <aside
        ref={sidebar}
        className={`absolute left-0 top-0 z-999 flex h-screen w-[16rem] flex-col overflow-y-auto bg-primary duration-300 ease-linear lg:static lg:translate-x-0 ${
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <div className="bg-primary sticky top-0 z-9999 px-4 py-5">
          <div className="flex items-center justify-between gap-2">
            <p className="text-transparent text-[0.1px]">.</p>
            <NavLink to="/dashboard" className="">
              <img src={logo} alt="Logo" className="w-40" />
            </NavLink>

            <FaAngleDoubleLeft
              ref={trigger}
              onClick={() => setSidebarOpen(!sidebarOpen)}
              aria-controls="sidebar"
              aria-expanded={sidebarOpen}
              className="block lg:hidden text-white cursor-pointer text-2xl"
            />
            <p className="text-transparent text-[0.1px] hidden lg:block">.</p>
          </div>
          <p
            className="text-center text-xs font-bold text-secondary mt-2 tracking-widest drop-shadow-lg animate-fadeIn"
            style={{
              fontFamily: "'Poppins', sans-serif",
              textShadow: "2px 2px 4px rgba(0,0,0,0.3)",
            }}
          >
            Merchant Portal
          </p>
        </div>

        {/* <div className="flex flex-col overflow-y-auto duration-300 ease-linear"> */}
        <div className="flex flex-col flex-1">
          {isLoading || !merchantPermissions ? (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center space-y-4 py-8">
                <Loader 
                  variant="dots" 
                  size="md" 
                  color="white" 
                  text="Loading menu..."
                />
              </div>
            </div>
          ) : (
            <nav className="pb-4 lg:pb-5 px-4 lg:px-6 text-lg font-normal">
              <h3 className="mb-3 text-sm font-semibold text-white/50 tracking-wide">
                MENU
              </h3>
              <ul className="mb-6 flex flex-col gap-1.5 text-white text-base">
                {sideBarMenus?.map(
                  (menu, index) =>
                    isAuthorizedToAccessMenu(menu.permission) && (
                    <li key={index}>
                      {menu.children ? (
                        <ChildLinkGroup
                          activeCondition={
                            pathname.includes(menu.parentRoute) ||
                            menu.children?.some((child) => pathname.startsWith(child.route))
                          }
                        >
                          {(handleClick, open) => (
                            <>
                              <NavLink
                                className={`group relative flex items-center justify-between rounded-sm py-2 px-2 my-1 duration-300 ease-in-out hover:bg-tertiary/60 hover:text-primary ${
                                  (pathname.includes(`${menu.parentRoute}`) ||
                                    menu.children?.some((child) => pathname.startsWith(child.route))) &&
                                  "bg-tertiary/80 text-primary"
                                }`}
                                onClick={(e) => {
                                  e.preventDefault();
                                  sidebarExpanded
                                    ? handleClick()
                                    : setSidebarExpanded(true);
                                }}
                              >
                                <div className="flex items-center gap-2 text-xs">
                                  {menu.icon}
                                  <span>{menu.title}</span>
                                </div>

                                {open ? (
                                  <FaAngleUp className="transition-transform" />
                                ) : (
                                  <FaAngleDown className="transition-transform" />
                                )}
                              </NavLink>
                              <div
                                className={`overflow-hidden transition-[max-height] duration-300 ease-in-out ${
                                  open ? "max-h-[500px]" : "max-h-0"
                                }`}
                              >
                                <ul className="pl-4 mt-2 flex flex-col gap-1.5 text-sm">
                                  {menu.children.map(
                                    (child, idx) =>
                                      isAuthorizedToAccessMenu(child.permission) && (
                                      <li key={idx}>
                                        <NavLink
                                          to={child.route}
                                          className={`group flex items-center gap-1.5 my-2 duration-200 ease-in-out hover:pl-1 text-xs  ${
                                            pathname.includes(`${child.route}`)
                                              ? "text-secondary"
                                              : "text-white"
                                          }`}
                                          onClick={handleLinkClick}
                                        >
                                          <GoDash />
                                          {child.title}
                                        </NavLink>
                                      </li>
                                    )
                                  )}
                                </ul>
                              </div>
                            </>
                          )}
                        </ChildLinkGroup>
                      ) : (
                        <NavLink
                          to={menu.route}
                          className={`group relative flex items-center justify-between rounded-sm py-2 px-2 my-2 text-sm duration-300 ease-in-out hover:bg-tertiary/60 hover:text-primary ${
                            pathname.includes(`${menu.route}`) &&
                            "bg-tertiary/80 text-primary"
                          }`}
                          onClick={handleLinkClick}
                        >
                          <div className="flex items-center gap-2 text-xs">
                            {menu.icon}
                            <span>{menu.title}</span>
                          </div>

                          {menu.title === "Refund Requests" && (
                            <span
                              className="bg-white text-primary text-xs rounded-full w-5 h-5 flex items-center justify-center font-semibold"
                              data-tooltip-id="my-tooltip"
                              data-tooltip-content="Pending Requests"
                            >
                              {refundRequests?.length}
                            </span>
                          )}
                        </NavLink>
                      )}
                    </li>
                  )
                )}
              </ul>
            </nav>
          )}
        </div>
      </aside>
    </ClickOutside>
  );
};

export default Sidebar;
